import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Row,
    Col,
    Button,
    Form,
    InputNumber,
    Input,
    DatePicker,
    ConfigProvider,
    Checkbox,
    Radio,
    Rate,
    Tooltip,
    Switch,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getExpertise, createOrUpdateExpertise } from '../../core/center-accreditation/centerAccreditationApi';

import './ScExpertise.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Text } = Typography;
const { TextArea } = Input;

// 7.1 Expert evaluation objects
const defaultExpertiseObjectTypes = [
    { code: 'programos', title: '7.1.1 Programos', count: 0 },
    { code: 'vadoveliai', title: '7.1.2 Vadovėliai', count: 0 },
    { code: 'metodinės_priemonės', title: '7.1.3 Metodinės priemonės', count: 0 },
    { code: 'pedagoginė_veikla', title: '7.1.4 Pedagoginė veikla', count: 0 },
    { code: 'mokytojų_kompetencijos', title: '7.1.5 Mokytojų, mokyklos vadovų kompetencijos', count: 0 },
    { code: 'mokyklos_organizacijos', title: '7.1.6 Mokyklos organizacijos', count: 0 },
    { code: 'kita', title: '7.1.7 Kita (įrašykite)', count: 0 },
];

// 7.2 Scope of expertise
const expertiseScopeTypes = [
    { code: 'mokyklos', title: '7.2.1 Mokyklos', count: 0 },
    { code: 'regiono', title: '7.2.2 Regiono', count: 0 },
    { code: 'šalies', title: '7.2.3 Šalies', count: 0 },
    { code: 'tarptautiniai', title: '7.2.4 Tarptautiniai', count: 0 },
];

// 7.3 Who applies for expertise?
const expertiseRequesters = [
    { code: 'mokytojai', title: '7.3.1 mokytojai', checked: false },
    { code: 'mokyklų_vadovai', title: '7.3.2 mokyklų vadovai, jų pavaduotojai ugdymui, ugdymą organizuojančių skyrių vedėjai', checked: false },
    { code: 'apskričių_viršininkų', title: '7.3.3 apskričių viršininkų, savivaldybių administracijų švietimo padalinių darbuotojai', checked: false },
    { code: 'kiti', title: '7.3.4 kiti (įrašykite)', checked: false },
];

// 7.4 Who do you invite as experts?
const expertTypes = [
    { code: 'institucijos_darbuotojus', title: '7.4.1 Institucijos darbuotojus', count: 0 },
    { code: 'mokytojus_praktikus', title: '7.4.2 Mokytojus praktikus', count: 0 },
    { code: 'mokslininkus_dėstytojus', title: '7.4.3 Lietuvos universitetų, kitų aukštųjų mokyklų ir mokslo institucijų mokslininkus, dėstytojus', count: 0 },
    { code: 'užsienio_mokslininkus', title: '7.4.4 Užsienio mokslininkus bei praktikus', count: 0 },
    { code: 'apskričių_viršininkų', title: '7.4.5 Apskričių viršininkų, savivaldybių administracijų švietimo padalinių darbuotojus', count: 0 },
    { code: 'kitus', title: '7.4.6 Kitus (įrašykite)', count: 0 },
];

// 7.5 What requirements do you set for experts?
const expertRequirements = [
    { code: 'formalų_plačioje', title: '7.5.1 turi formalų kompetencijos pripažinimą plačioje tematikos srityje', checked: false },
    { code: 'formalų_siauroje', title: '7.5.2 turi formalų kompetencijos pripažinimą siauroje ekspertuojamos temos kryptyje', checked: false },
    { code: 'neformalų_plačioje', title: '7.5.3 turi neformalų kompetencijos pripažinimą plačioje tematikos srityje', checked: false },
    { code: 'neformalų_siauroje', title: '7.5.4 turi neformalų kompetencijos pripažinimą siauroje ekspertuojamos temos kryptyje', checked: false },
    { code: 'kita', title: '7.5.5 kita (įrašykite)', checked: false },
];

// 7.6 What goals are pursued when conducting expertise?
const expertiseGoals = [
    { code: 'kompetencijos_įvertinimo', title: '7.6.1 Kompetencijos įvertinimo', rating: 0 },
    { code: 'darbo_rezultatų_įvertinimo', title: '7.6.2 Darbo rezultatų įvertinimo', rating: 0 },
    { code: 'tobulėjimo_gairių', title: '7.6.3 Tobulėjimo gairių numatymo', rating: 0 },
    { code: 'gerosios_patirties', title: '7.6.4 Gerosios ir/ar pažangiausiosios patirties nustatymo', rating: 0 },
    { code: 'trūkumų_nustatymo', title: '7.6.5 Trūkumų nustatymo', rating: 0 },
    { code: 'kita', title: '7.6.6 Kita (įrašykite)', rating: 0 },
];

// 7.8 How do you find out if clients consider the performed expertise objective and reliable?
const expertiseFeedbackMethods = [
    { code: 'klausiate_po_įrodymus', title: '7.8.1 to klausiate po ekspertizės ir kilus nesutarimams pateikiate papildomus ekspertizės objektyvumo įrodymus', checked: false },
    { code: 'klausiate_po_diskutuojate', title: '7.8.2 to klausiate po ekspertizės ir kilus nesutarimams diskutuojate', checked: false },
    { code: 'pasidomite_neformaliai', title: '7.8.3 pasidomite neformaliai', checked: false },
    { code: 'manote_nediskutuotinas', title: '7.8.4 manote, jog ekspertizės objektyvumas nediskutuotinas', checked: false },
    { code: 'kita', title: '7.8.5 kita (įrašykite)', checked: false },
];

const ScExpertise: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { expertise, isExpertiseFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [expertiseObjectTypesOther, setExpertiseObjectTypesOther] = useState<string>('');
    const [expertiseRequestersOther, setExpertiseRequestersOther] = useState<string>('');
    const [expertTypesOther, setExpertTypesOther] = useState<string>('');
    const [expertRequirementsOther, setExpertRequirementsOther] = useState<string>('');
    const [expertiseGoalsOther, setExpertiseGoalsOther] = useState<string>('');
    const [expertiseFeedbackMethodsOther, setExpertiseFeedbackMethodsOther] = useState<string>('');

    useEffect(() => {
        dispatch(getExpertise({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    useEffect(() => {
        if (expertise) {
            // Transform expertise_object_types from array to form format
            const expertiseObjectTypesValue = (expertise.expertise_object_types || defaultExpertiseObjectTypes).map((item: any) => {
                // For "kita", we store both count and text
                if (item.code === 'kita') {
                    let otherText = '';
                    let count = 0;
                    const itemCount = item.count;

                    // Check if count contains other text (format: "0_other_text" or similar)
                    if (typeof itemCount === 'string' && itemCount.includes('_other_')) {
                        const parts = itemCount.split('_other_');
                        count = Number(parts[0]) || 0;
                        otherText = parts[1] || '';
                    } else {
                        count = Number(itemCount) || 0;
                    }

                    setExpertiseObjectTypesOther(otherText);

                    return {
                        code: item.code,
                        title: item.title,
                        count,
                    };
                }

                return {
                    code: item.code,
                    title: item.title,
                    count: Number(item.count) || 0,
                };
            });

            // Transform expertise_scope from array to form format
            const expertiseScopeValue = (expertise.expertise_scope || expertiseScopeTypes).map((item: any) => ({
                code: item.code,
                title: item.title,
                count: Number(item.count) || 0,
            }));

            // Transform expertise_requesters from array to form format
            const expertiseRequestersValue = (expertise.expertise_requesters || expertiseRequesters).map((item: any) => ({
                code: item.code,
                title: item.title,
                checked: item.checked || false,
                other: item.other || '',
            }));

            // Find "kiti" requester and extract other text
            const kitiRequester = expertiseRequestersValue.find((item: any) => item.code === 'kiti');
            if (kitiRequester) {
                setExpertiseRequestersOther(kitiRequester.other || '');
            }

            // Transform expert_types from array to form format
            // Filter out "iš_viso" items as they are calculated dynamically
            const expertTypesValue = (expertise.expert_types || expertTypes)
                .filter((item: any) => item.code !== 'iš_viso')
                .map((item: any) => {
                    let { count } = item;
                    let otherText = '';

                    // Check if count contains other text (format: "0_other_text" or similar)
                    if (typeof count === 'string' && count.includes('_other_')) {
                        const parts = count.split('_other_');
                        count = Number(parts[0]) || 0;
                        otherText = parts[1] || '';
                    } else {
                        count = Number(count) || 0;
                    }

                    // If this is the "kitus" item, set the other text
                    if (item.code === 'kitus' && otherText) {
                        setExpertTypesOther(otherText);
                    }

                    return {
                        code: item.code,
                        title: item.title,
                        count,
                    };
                });

            // Transform expert_requirements from array to form format
            const expertRequirementsValue = (expertise.expert_requirements || expertRequirements).map((item: any) => ({
                code: item.code,
                title: item.title,
                checked: item.checked || false,
                other: item.other || '',
            }));

            // Find "kita" requirement and extract other text
            const kitaRequirement = expertRequirementsValue.find((item: any) => item.code === 'kita');
            if (kitaRequirement) {
                setExpertRequirementsOther(kitaRequirement.other || '');
            }

            // Transform expertise_goals from array to form format
            const expertiseGoalsValue = (expertise.expertise_goals || expertiseGoals).map((item: any) => ({
                code: item.code,
                title: item.title,
                rating: Number(item.rating) || 0,
                other: item.other || '',
            }));

            // Find "kita" goal and extract other text
            const kitaGoal = expertiseGoalsValue.find((item: any) => item.code === 'kita');
            if (kitaGoal) {
                setExpertiseGoalsOther(kitaGoal.other || '');
            }

            // Transform expertise_feedback_methods from array to form format
            const expertiseFeedbackMethodsValue = (expertise.expertise_feedback_methods || expertiseFeedbackMethods).map((item: any) => ({
                code: item.code,
                title: item.title,
                checked: item.checked || false,
                other: item.other || '',
            }));

            // Find "kita" feedback method and extract other text
            const kitaFeedback = expertiseFeedbackMethodsValue.find((item: any) => item.code === 'kita');
            if (kitaFeedback) {
                setExpertiseFeedbackMethodsOther(kitaFeedback.other || '');
            }

            form.setFieldsValue({
                expertise_object_types: expertiseObjectTypesValue,
                expertise_scope: expertiseScopeValue,
                expertise_requesters: expertiseRequestersValue,
                expert_types: expertTypesValue,
                expert_requirements: expertRequirementsValue,
                expertise_goals: expertiseGoalsValue,
                is_expertise_regulated: expertise.is_expertise_regulated || false,
                regulation_documents: expertise.regulation_documents || '',
                criteria_documents: expertise.criteria_documents || '',
                expert_panel_composition: expertise.expert_panel_composition || {},
                expert_panel_selection: expertise.expert_panel_selection || '',
                dispute_resolution_procedure: expertise.dispute_resolution_procedure || '',
                expertise_trend: expertise.expertise_trend || null,
                are_assessments_recorded: expertise.are_assessments_recorded || false,
                expertise_feedback_methods: expertiseFeedbackMethodsValue,
            });
        } else {
            form.setFieldsValue({
                expertise_object_types: defaultExpertiseObjectTypes,
                expertise_scope: expertiseScopeTypes,
                expertise_requesters: expertiseRequesters,
                expert_types: expertTypes,
                expert_requirements: expertRequirements,
                expertise_goals: expertiseGoals,
                is_expertise_regulated: false,
                regulation_documents: '',
                criteria_documents: '',
                expert_panel_composition: {},
                expert_panel_selection: '',
                dispute_resolution_procedure: '',
                expertise_trend: null,
                are_assessments_recorded: false,
                expertise_feedback_methods: expertiseFeedbackMethods,
            });
        }
    }, [expertise, form]);

    const handleSave = async (values: any) => {
        // Transform expertise_object_types
        const expertiseObjectTypes = (values.expertise_object_types || []).map((item: any) => {
            if (item.code === 'kita') {
                // For "kita", store count and text together
                const count = Number(item.count) || 0;
                return {
                    code: item.code,
                    title: item.title,
                    count: expertiseObjectTypesOther ? `${count}_other_${expertiseObjectTypesOther}` : count,
                };
            }
            return {
                code: item.code,
                title: item.title,
                count: Number(item.count) || 0,
            };
        });

        // Transform expertise_scope
        const expertiseScope = (values.expertise_scope || []).map((item: any) => ({
            code: item.code,
            title: item.title,
            count: Number(item.count) || 0,
        }));

        // Calculate total for expertise_scope
        const scopeTotal = expertiseScope.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
        expertiseScope.push({
            code: 'iš_viso',
            title: '7.2.5 Iš viso',
            count: scopeTotal,
        });

        // Transform expertise_requesters
        const expertiseRequestersData = (values.expertise_requesters || []).map((item: any) => ({
            code: item.code,
            title: item.title,
            checked: item.checked || false,
            other: item.code === 'kiti' ? expertiseRequestersOther : '',
        }));

        // Transform expert_types
        const expertTypesData = (values.expert_types || []).map((item: any) => {
            let count: number | string = Number(item.count) || 0;
            if (item.code === 'kitus' && expertTypesOther) {
                count = `0_other_${expertTypesOther}`;
            }
            return {
                code: item.code,
                title: item.title,
                count,
            };
        });

        // Calculate total for expert_types
        const expertTypesTotal = expertTypesData.reduce((sum: number, item: any) => {
            const count = typeof item.count === 'string' ? 0 : Number(item.count) || 0;
            return sum + count;
        }, 0);
        expertTypesData.push({
            code: 'iš_viso',
            title: '7.4.7 Iš viso',
            count: expertTypesTotal,
        });

        // Transform expert_requirements
        const expertRequirementsData = (values.expert_requirements || []).map((item: any) => ({
            code: item.code,
            title: item.title,
            checked: item.checked || false,
            other: item.code === 'kita' ? expertRequirementsOther : '',
        }));

        // Transform expertise_goals
        const expertiseGoalsData = (values.expertise_goals || []).map((item: any) => ({
            code: item.code,
            title: item.title,
            rating: Number(item.rating) || 0,
            other: item.code === 'kita' ? expertiseGoalsOther : '',
        }));

        // Transform expertise_feedback_methods
        const expertiseFeedbackMethodsData = (values.expertise_feedback_methods || []).map((item: any) => ({
            code: item.code,
            title: item.title,
            checked: item.checked || false,
            other: item.code === 'kita' ? expertiseFeedbackMethodsOther : '',
        }));

        // Transform expert_panel_composition
        const expertPanelComposition = values.expert_panel_composition || {};

        const requestData: any = {
            ...(expertise?.id && { id: expertise.id }),
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            expertise_object_types: expertiseObjectTypes,
            expertise_scope: expertiseScope,
            expertise_requesters: expertiseRequestersData,
            expert_types: expertTypesData,
            expert_requirements: expertRequirementsData,
            expertise_goals: expertiseGoalsData,
            is_expertise_regulated: values.is_expertise_regulated || false,
            regulation_documents: values.regulation_documents || null,
            criteria_documents: values.criteria_documents || null,
            expert_panel_composition: Object.keys(expertPanelComposition).length > 0 ? expertPanelComposition : null,
            expert_panel_selection: values.expert_panel_selection || null,
            dispute_resolution_procedure: values.dispute_resolution_procedure || null,
            expertise_trend: values.expertise_trend || null,
            are_assessments_recorded: values.are_assessments_recorded || false,
            expertise_feedback_methods: expertiseFeedbackMethodsData,
        };

        await dispatch(createOrUpdateExpertise(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getExpertise({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="school-expertise">
                <Card title="Ekspertizė (Direktorė)" className="expertise-form-card" loading={isExpertiseFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {expertise?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {expertise.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti ekspertizės duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isExpertiseFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            expertise_object_types: defaultExpertiseObjectTypes,
                            expertise_scope: expertiseScopeTypes,
                            expertise_requesters: expertiseRequesters,
                            expert_types: expertTypes,
                            expert_requirements: expertRequirements,
                            expertise_goals: expertiseGoals,
                            is_expertise_regulated: false,
                            regulation_documents: '',
                            criteria_documents: '',
                            expert_panel_composition: {},
                            expert_panel_selection: '',
                            dispute_resolution_procedure: '',
                            expertise_trend: null,
                            are_assessments_recorded: false,
                            expertise_feedback_methods: expertiseFeedbackMethods,
                        }}>

                        {/* 7.1 Expert evaluation objects */}
                        <Card title="7.1 Klausimai apie veiklos turinį - Ekspertinio vertinimo objektai (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="expertise_object_types">
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => prevValues.expertise_object_types !== currentValues.expertise_object_types}>
                                    {() => {
                                        const expertiseObjectTypes = form.getFieldValue('expertise_object_types') || defaultExpertiseObjectTypes;
                                        const total = expertiseObjectTypes.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                                        return (
                                            <Form.List name="expertise_object_types">
                                                {(fields) => (
                                                    <>
                                                        {fields.map((field, index) => {
                                                            const item = expertiseObjectTypes[index] || defaultExpertiseObjectTypes[index];
                                                            const isOther = item?.code === 'kita';
                                                            return (
                                                                <Row key={field.key} gutter={16} style={{ marginBottom: 16 }}>
                                                                    <Col span={16}>
                                                                        <Text>{item?.title}</Text>
                                                                    </Col>
                                                                    <Col span={8}>
                                                                        {isOther ? (
                                                                            <>
                                                                                <Form.Item
                                                                                    name={[field.name, 'count']}
                                                                                    style={{ marginBottom: 8 }}>
                                                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                                                </Form.Item>
                                                                                <Form.Item>
                                                                                    <Input
                                                                                        placeholder="Įrašykite"
                                                                                        value={expertiseObjectTypesOther}
                                                                                        onChange={(e) => setExpertiseObjectTypesOther(e.target.value)} />
                                                                                </Form.Item>
                                                                            </>
                                                                        ) : (
                                                                            <Form.Item name={[field.name, 'count']} style={{ margin: 0 }}>
                                                                                <InputNumber min={0} style={{ width: '100%' }} />
                                                                            </Form.Item>
                                                                        )}
                                                                    </Col>
                                                                </Row>
                                                            );
                                                        })}
                                                        <Row gutter={16} style={{ marginBottom: 16 }}>
                                                            <Col span={16}>
                                                                <Text strong>7.1.9 Iš viso</Text>
                                                            </Col>
                                                            <Col span={8}>
                                                                <Text strong>{total}</Text>
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                            </Form.List>
                                        );
                                    }}
                                </Form.Item>
                            </Form.Item>
                        </Card>

                        {/* 7.2 Scope of expertise */}
                        <Card title="7.2 Ekspertizės mastai (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="expertise_scope">
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => prevValues.expertise_scope !== currentValues.expertise_scope}>
                                    {() => {
                                        const expertiseScope = form.getFieldValue('expertise_scope') || expertiseScopeTypes;
                                        const total = expertiseScope.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                                        return (
                                            <Form.List name="expertise_scope">
                                                {(fields) => (
                                                    <>
                                                        {fields.map((field, index) => {
                                                            const item = expertiseScope[index] || expertiseScopeTypes[index];
                                                            return (
                                                                <Row key={field.key} gutter={16} style={{ marginBottom: 16 }}>
                                                                    <Col span={16}>
                                                                        <Text>{item?.title}</Text>
                                                                    </Col>
                                                                    <Col span={8}>
                                                                        <Form.Item name={[field.name, 'count']} style={{ margin: 0 }}>
                                                                            <InputNumber min={0} style={{ width: '100%' }} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                </Row>
                                                            );
                                                        })}
                                                        <Row gutter={16} style={{ marginBottom: 16 }}>
                                                            <Col span={16}>
                                                                <Text strong>7.2.5 Iš viso</Text>
                                                            </Col>
                                                            <Col span={8}>
                                                                <Text strong>{total}</Text>
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                            </Form.List>
                                        );
                                    }}
                                </Form.Item>
                            </Form.Item>
                        </Card>

                        {/* 7.3 Who applies for expertise? */}
                        <Card title="7.3 Kas kreipiasi ekspertizės?" style={{ marginBottom: 16 }}>
                            <Form.List name="expertise_requesters">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const item = form.getFieldValue(['expertise_requesters', index]) || expertiseRequesters[index];
                                            const isOther = item?.code === 'kiti';
                                            return (
                                                <div key={field.key} style={{ marginBottom: 16 }}>
                                                    <Form.Item name={[field.name, 'checked']} valuePropName="checked" style={{ marginBottom: isOther ? 8 : 0 }}>
                                                        <Checkbox>{item?.title}</Checkbox>
                                                    </Form.Item>
                                                    {isOther && (
                                                        <Form.Item>
                                                            <Input
                                                                placeholder="Įrašykite"
                                                                value={expertiseRequestersOther}
                                                                onChange={(e) => setExpertiseRequestersOther(e.target.value)} />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* 7.4 Who do you invite as experts? */}
                        <Card title="7.4 Ką kviečiate ekspertais? (nurodykite ekspertizių skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="expert_types">
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => prevValues.expert_types !== currentValues.expert_types}>
                                    {() => {
                                        const expertTypesValues = form.getFieldValue('expert_types') || expertTypes;
                                        const total = expertTypesValues.reduce((sum: number, item: any) => {
                                            const count = typeof item.count === 'string' ? 0 : Number(item.count) || 0;
                                            return sum + count;
                                        }, 0);
                                        return (
                                            <Form.List name="expert_types">
                                                {(fields) => (
                                                    <>
                                                        {fields.map((field, index) => {
                                                            const item = expertTypesValues[index] || expertTypes[index];
                                                            const isOther = item?.code === 'kitus';
                                                            return (
                                                                <Row key={field.key} gutter={16} style={{ marginBottom: 16 }}>
                                                                    <Col span={16}>
                                                                        <Text>{item?.title}</Text>
                                                                    </Col>
                                                                    <Col span={8}>
                                                                        {isOther ? (
                                                                            <>
                                                                                <Form.Item
                                                                                    name={[field.name, 'count']}
                                                                                    style={{ marginBottom: 8 }}>
                                                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                                                </Form.Item>
                                                                                <Form.Item>
                                                                                    <Input
                                                                                        placeholder="Įrašykite"
                                                                                        value={expertTypesOther}
                                                                                        onChange={(e) => setExpertTypesOther(e.target.value)} />
                                                                                </Form.Item>
                                                                            </>
                                                                        ) : (
                                                                            <Form.Item name={[field.name, 'count']} style={{ margin: 0 }}>
                                                                                <InputNumber min={0} style={{ width: '100%' }} />
                                                                            </Form.Item>
                                                                        )}
                                                                    </Col>
                                                                </Row>
                                                            );
                                                        })}
                                                        <Row gutter={16} style={{ marginBottom: 16 }}>
                                                            <Col span={16}>
                                                                <Text strong>7.4.7 Iš viso</Text>
                                                            </Col>
                                                            <Col span={8}>
                                                                <Text strong>{total}</Text>
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                            </Form.List>
                                        );
                                    }}
                                </Form.Item>
                            </Form.Item>
                        </Card>

                        {/* 7.5 What requirements do you set for experts? */}
                        <Card title="7.5 Kokius reikalavimus keliate ekspertams?" style={{ marginBottom: 16 }}>
                            <Form.List name="expert_requirements">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const item = form.getFieldValue(['expert_requirements', index]) || expertRequirements[index];
                                            const isOther = item?.code === 'kita';
                                            return (
                                                <div key={field.key} style={{ marginBottom: 16 }}>
                                                    <Form.Item name={[field.name, 'checked']} valuePropName="checked" style={{ marginBottom: isOther ? 8 : 0 }}>
                                                        <Checkbox>{item?.title}</Checkbox>
                                                    </Form.Item>
                                                    {isOther && (
                                                        <Form.Item>
                                                            <Input
                                                                placeholder="Įrašykite"
                                                                value={expertRequirementsOther}
                                                                onChange={(e) => setExpertRequirementsOther(e.target.value)} />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* 7.6 What goals are pursued when conducting expertise? */}
                        <Card title="7.6 Kokių tikslų siekiama atliekant ekspertizę?" style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 16 }}>
                                <Text type="secondary">Įvertinkite balais nuo 0 iki 4 (4 - dažniausiai)</Text>
                            </div>
                            <Form.List name="expertise_goals">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const item = form.getFieldValue(['expertise_goals', index]) || expertiseGoals[index];
                                            const isOther = item?.code === 'kita';
                                            return (
                                                <div key={field.key} style={{ marginBottom: 16 }}>
                                                    <Row gutter={16}>
                                                        <Col span={16}>
                                                            <Text>{item?.title}</Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Form.Item name={[field.name, 'rating']} style={{ margin: 0 }}>
                                                                <Rate count={4} />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    {isOther && (
                                                        <Form.Item style={{ marginTop: 8 }}>
                                                            <Input
                                                                placeholder="Įrašykite"
                                                                value={expertiseGoalsOther}
                                                                onChange={(e) => setExpertiseGoalsOther(e.target.value)} />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* 7.7 Are assessments of conducted expertise recorded? */}
                        <Card title="7.7 Ar fiksuojami atliekamų ekspertizių vertinimai?" style={{ marginBottom: 16 }}>
                            <Form.Item name="are_assessments_recorded">
                                <Radio.Group>
                                    <Radio value>– taip</Radio>
                                    <Radio value={false}>– ne</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 7.8 How do you find out if clients consider the performed expertise objective and reliable? */}
                        <Card title="7.8 Kaip sužinote, ar klientai atliktą ekspertizę laiko objektyvia ir patikima?" style={{ marginBottom: 16 }}>
                            <Form.List name="expertise_feedback_methods">
                                {(fields) => (
                                    <>
                                        {fields.map((field, index) => {
                                            const item = form.getFieldValue(['expertise_feedback_methods', index]) || expertiseFeedbackMethods[index];
                                            const isOther = item?.code === 'kita';
                                            return (
                                                <div key={field.key} style={{ marginBottom: 16 }}>
                                                    <Form.Item name={[field.name, 'checked']} valuePropName="checked" style={{ marginBottom: isOther ? 8 : 0 }}>
                                                        <Checkbox>{item?.title}</Checkbox>
                                                    </Form.Item>
                                                    {isOther && (
                                                        <Form.Item>
                                                            <Input
                                                                placeholder="Įrašykite"
                                                                value={expertiseFeedbackMethodsOther}
                                                                onChange={(e) => setExpertiseFeedbackMethodsOther(e.target.value)} />
                                                        </Form.Item>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </Form.List>
                        </Card>

                        {/* Additional fields */}
                        <Card title="Papildoma informacija" style={{ marginBottom: 16 }}>
                            <Form.Item name="is_expertise_regulated" label="Ar ekspertizė reglamentuota?" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                            <Form.Item name="regulation_documents" label="Reglamentavimo dokumentai">
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item name="criteria_documents" label="Kriterijų dokumentai">
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item name="expert_panel_selection" label="Ekspertų komisijos sudarymas">
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item name="dispute_resolution_procedure" label="Nesutarimų sprendimo tvarka">
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item name="expertise_trend" label="Ekspertizės tendencija">
                                <Radio.Group>
                                    <Radio value="dideja">Didėja</Radio>
                                    <Radio value="mazeja">Mažėja</Radio>
                                    <Radio value="nekinta">Nekinta</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScExpertise;
